// First-touch signup-attribution capture, ported and tightened from
// apps/wir/src/lib/utm.ts. Morpheus accepts these as flat, snake_case,
// top-level fields on POST /oauth/register and writes them to
// oauth_users.signup_attribution when the account is created at verify.
//
// NOTE: this is a deliberate copy of apps/accounts/src/lib/attribution.ts —
// connect (Vite) and accounts (Next) are separately-bundled apps and we keep a
// local copy in each rather than a shared workspace package. Keep the two in
// sync when either changes.
//
// Everything degrades to {} rather than throwing: the sign-up entry point is a
// public page and a storage error (Safari private mode, disabled storage) must
// never block a sign-up.

/** The exact nine fields Morpheus accepts. No wir-internal `ref`/`cta` — those
 *  aren't acquisition sources and Morpheus would strip them anyway. */
export const ATTRIBUTION_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid', // Google Ads click id
  'fbclid', // Facebook click id
  'referrer',
  'landing_page',
] as const

/** The single localStorage key holding connect's first-touch capture. Exported
 *  because main.tsx has to BOTH write it and exempt it from its periodic
 *  localStorage cleaner — the cleaner keeps a whitelist and wiped this key one
 *  tick after capture, so the campaign never reached accounts. */
export const ATTRIBUTION_STORAGE_KEY = 'stampede.attribution'

export type AttributionKey = (typeof ATTRIBUTION_KEYS)[number]
export type SignupAttribution = Partial<Record<AttributionKey, string>>

/** Morpheus validates with a strict Zod schema, so an over-length value (a long
 *  ad URL as `landing_page` can easily top 500 chars) 422s the WHOLE
 *  registration. Truncate on write AND on read so a value stored before these
 *  limits existed can't slip through either. */
export const MAX_LENGTHS: Record<AttributionKey, number> = {
  utm_source: 255,
  utm_medium: 255,
  utm_campaign: 255,
  utm_term: 255,
  utm_content: 255,
  gclid: 255,
  fbclid: 255,
  referrer: 500,
  landing_page: 500,
}

function truncate(key: AttributionKey, value: string): string {
  return value.slice(0, MAX_LENGTHS[key])
}

/** Every acquisition key EXCEPT `landing_page` — `landing_page` is always
 *  present (it's just the current URL), so on its own it isn't evidence a
 *  visitor arrived from a campaign. Used to decide whether a capture is worth
 *  persisting at all. */
function hasSignal(attribution: SignupAttribution): boolean {
  return ATTRIBUTION_KEYS.some(
    (key) => key !== 'landing_page' && attribution[key] !== undefined
  )
}

/** Coerce an unknown parsed blob into a clean, truncated SignupAttribution. */
function normalise(source: unknown): SignupAttribution {
  if (typeof source !== 'object' || source === null) return {}
  const record = source as Record<string, unknown>
  const out: SignupAttribution = {}
  for (const key of ATTRIBUTION_KEYS) {
    const value = record[key]
    if (typeof value === 'string' && value.length > 0) {
      out[key] = truncate(key, value)
    }
  }
  return out
}

/** Pull the nine keys out of an incoming query string, truncated. This is how
 *  attribution crosses the connect → accounts handover (forwarded as query
 *  params) and how accounts reads a directly-UTM'd /register URL. */
export function attributionFromParams(
  params: URLSearchParams
): SignupAttribution {
  const out: SignupAttribution = {}
  for (const key of ATTRIBUTION_KEYS) {
    const value = params.get(key)
    if (value) out[key] = truncate(key, value)
  }
  return out
}

/** `referrer` + `landing_page` as seen on the CURRENT origin. `referrer` is
 *  dropped when it's same-origin — an internal navigation isn't an acquisition
 *  source. `landing_page` is the full current URL. SSR-safe. These two are
 *  imperfect (we capture them wherever they're first seen) but sent anyway. */
export function originAttribution(): SignupAttribution {
  if (typeof window === 'undefined') return {}
  const out: SignupAttribution = {}
  const referrer = document.referrer
  if (referrer && !referrer.startsWith(window.location.origin)) {
    out.referrer = truncate('referrer', referrer)
  }
  out.landing_page = truncate('landing_page', window.location.href)
  return out
}

/** The persisted first-touch capture, truncated on read. `{}` if absent, empty,
 *  malformed, or storage is unavailable. */
export function readAttribution(storageKey: string): SignupAttribution {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.localStorage.getItem(storageKey)
    if (!raw) return {}
    return normalise(JSON.parse(raw))
  } catch {
    return {}
  }
}

/** Write-once persist: stores `attribution` only when the key is currently
 *  empty AND the capture carries a real acquisition signal, so a later clean
 *  load or an internal /register → / → /register bounce never overwrites an
 *  earlier first-touch capture, and a bare `landing_page` never blocks a later
 *  paid click from being captured. */
export function persistAttribution(
  storageKey: string,
  attribution: SignupAttribution
): void {
  if (typeof window === 'undefined') return
  if (!hasSignal(attribution)) return
  if (Object.keys(readAttribution(storageKey)).length > 0) return
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(attribution))
  } catch {
    // ignore — private mode / disabled storage
  }
}

/** First-touch, write-once capture into localStorage. Returns early with the
 *  existing capture if the key already holds one, so the FIRST landing wins for
 *  the life of the browser (until cleared on a successful sign-up). Reads the
 *  UTM/click-id keys from the URL and `referrer`/`landing_page` from the origin,
 *  and only writes when a real acquisition signal survived — a clean organic
 *  navigation stores nothing and so never blocks a later paid click. */
export function captureAttribution(storageKey: string): SignupAttribution {
  if (typeof window === 'undefined') return {}

  const existing = readAttribution(storageKey)
  if (Object.keys(existing).length > 0) return existing

  const captured: SignupAttribution = {
    ...originAttribution(),
    ...attributionFromParams(new URLSearchParams(window.location.search)),
  }

  if (!hasSignal(captured)) return {}

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(captured))
  } catch {
    // ignore — private mode / disabled storage
  }
  return captured
}

/** Remove the store. Called synchronously on a successful sign-up so a second
 *  sign-up in the same browser can't inherit a stale campaign. */
export function clearAttribution(storageKey: string): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(storageKey)
  } catch {
    // ignore — private mode / disabled storage
  }
}

/**
 * Name of the cross-subdomain handover cookie.
 *
 * localStorage is per-origin, so a capture made on accounts.* is invisible to
 * product.*. A cookie scoped to the shared parent domain (stampede.ai or
 * stage.stampede.ai) is readable by both.
 *
 * UTMs only: no landing_page (it can carry an authorization code) and no click
 * ids or referrer.
 */
export const ATTRIBUTION_COOKIE_NAME = 'stampede_utm'

/** How long the handover cookie lives: 30 days, in seconds. */
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30

/**
 * The subset of attribution keys allowed into the handover cookie. Kept
 * separate from ATTRIBUTION_KEYS so click ids, referrer and landing_page can
 * never leak into it.
 */
const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const

/**
 * Resolve the cookie `Domain` for the current host by dropping its first label,
 * so the cookie is shared across sibling subdomains.
 *
 * `accounts.stage.stampede.ai` becomes `stage.stampede.ai`.
 *
 * @returns The parent domain, or `undefined` (a host-only cookie) for
 * localhost and other hosts with fewer than three labels, where there is
 * nothing to share.
 */
function cookieDomain(): string | undefined {
  const labels = window.location.hostname.split('.')
  if (labels.length < 3) return undefined
  return labels.slice(1).join('.')
}

/**
 * Write the UTM subset of `attribution` as a parent-domain cookie.
 *
 * Values are truncated to their Morpheus limits. Does nothing when no UTM is
 * set, and never throws if cookies are disabled.
 */
export function writeAttributionCookie(attribution: SignupAttribution): void {
  if (typeof document === 'undefined') return
  const utm: SignupAttribution = {}
  for (const key of UTM_KEYS) {
    const value = attribution[key]
    if (value) utm[key] = truncate(key, value)
  }
  if (Object.keys(utm).length === 0) return
  const domain = cookieDomain()
  try {
    document.cookie = [
      `${ATTRIBUTION_COOKIE_NAME}=${encodeURIComponent(JSON.stringify(utm))}`,
      'Path=/',
      `Max-Age=${String(COOKIE_MAX_AGE_SECONDS)}`,
      'SameSite=Lax',
      ...(window.location.protocol === 'https:' ? ['Secure'] : []),
      ...(domain ? [`Domain=${domain}`] : []),
    ].join('; ')
  } catch {
    // ignore — cookies disabled
  }
}

/**
 * Read the UTM subset back out of the handover cookie.
 *
 * @returns The cleaned, truncated UTMs, or `{}` if the cookie is absent,
 * malformed or cookies are unavailable.
 */
export function readAttributionCookie(): SignupAttribution {
  if (typeof document === 'undefined') return {}
  try {
    const entry = document.cookie
      .split('; ')
      .find((part) => part.startsWith(`${ATTRIBUTION_COOKIE_NAME}=`))
    if (!entry) return {}
    const raw = decodeURIComponent(
      entry.slice(ATTRIBUTION_COOKIE_NAME.length + 1)
    )
    const parsed: unknown = JSON.parse(raw)
    const out = normalise(parsed)
    for (const key of ATTRIBUTION_KEYS) {
      if (!(UTM_KEYS as readonly string[]).includes(key)) delete out[key]
    }
    return out
  } catch {
    return {}
  }
}

/**
 * Expire the handover cookie.
 *
 * It must be cleared with the same Domain and Path it was set with, or the
 * browser treats it as a different cookie and leaves it in place.
 */
export function clearAttributionCookie(): void {
  if (typeof document === 'undefined') return
  const domain = cookieDomain()
  try {
    document.cookie = [
      `${ATTRIBUTION_COOKIE_NAME}=`,
      'Path=/',
      'Max-Age=0',
      ...(domain ? [`Domain=${domain}`] : []),
    ].join('; ')
  } catch {
    // ignore — cookies disabled
  }
}
